/**
 * Production operator rosters — who drives the fleet, per mine × class × crew.
 *
 * **Synthesised for the proof of concept.** The extracted data pack has no
 * shift split and no operator on `shift_daily`, and `rms.json`
 * `rms_behaviour` names only four operators across 94 machine-weeks (it is an
 * operator-of-record summary, not a shift log). Routing operator issues to
 * the Operating Supervisor needs a roster, so this seed builds one:
 *
 * - **Four crews per site** (`A`–`D`), each with one operator per machine of
 *   each class — the operator's `homeMachine`. 100 machines × 4 crews = 400
 *   operators.
 * - **Real names first.** The nine operators in `people.json` `people`
 *   (joined by name, which is that table's key) are placed on their site and
 *   class, flagged `synthesised: false`. Every other operator is a generated
 *   stand-in, flagged `synthesised: true`, named deterministically from the
 *   pools below.
 * - **Roster pattern per site** — see {@link ROSTER_PATTERNS}. Owner-operated
 *   sites run a 4-panel 7/7 day-night rotation; the two contractor sites run
 *   the pattern their contractor would. Mount Isolde (Redgum, FIFO) runs
 *   fixed-shift 14-day swings, which is why the whole data window there is one
 *   day crew and one night crew.
 * - **Story pins and splits** — {@link STORY_PINS} and {@link STORY_SPLITS}
 *   are the three narrative machines the reviewer walkthrough leans on. They
 *   are declared here, as data, so the engine stays generic and the story is
 *   visible in one place.
 *
 * Data Schema layer — seed data. The roster is generated at module load from
 * the eager fleet with a seeded hash; no randomness, no clock.
 *
 * @see src/versions/v1/schema/operations.ts — `OperatorRecord`, `RosterPattern`
 * @see docs/engines/reliability.md § Provenance
 */

import { FLEET } from '../../data/core.ts';
import type { MachineClass, MineCode } from '../../schema/machine.ts';
import type { CrewCode, OperatorRecord, RosterPattern, ShiftCode } from '../../schema/operations.ts';
import { unit } from '../reliability/hash.ts';

/** The four crews, in roster order. */
export const CREWS: readonly CrewCode[] = ['A', 'B', 'C', 'D'];

/** A site's crew rotation. */
export interface SiteRoster {
  readonly pattern: RosterPattern;
  /** First day of block 0, `YYYY-MM-DD`. */
  readonly anchor: string;
  /** Why this pattern, in one line. */
  readonly note: string;
}

/**
 * Per-site roster pattern. Anchors are chosen so that every owner-operated
 * site changes block inside the 29 Aug – 08 Sep window (all four crews
 * appear), and Mount Isolde's swing covers the whole window.
 */
export const ROSTER_PATTERNS: Readonly<Record<MineCode, SiteRoster>> = {
  KG: { pattern: '4-panel', anchor: '2026-08-26', note: 'Owner-operated, residential — 7 days, 7 off, 7 nights, 7 off.' },
  BR: { pattern: '4-panel', anchor: '2026-08-24', note: 'Owner-operated, DIDO from Dubbo — 7/7 day-night rotation.' },
  MI: {
    pattern: 'fifo-fixed',
    anchor: '2026-08-26',
    note: 'Redgum Mining Services, FIFO from Townsville — 14-day swings, shift fixed for the swing (A/C days, B/D nights).',
  },
  OH: { pattern: '4-panel', anchor: '2026-08-27', note: 'Owner-operated surface drill crews — 7/7 day-night rotation.' },
  GD: { pattern: '4-panel', anchor: '2026-08-25', note: 'Owner crews and Spinifex contractor crews on the same 7/7 rotation.' },
  MR: { pattern: '4-panel', anchor: '2026-08-29', note: 'Western Tiers Mining Services — 7/7 day-night rotation.' },
};

/** Three-letter class tag used in operator ids. */
export const CLASS_TAG: Readonly<Record<MachineClass, string>> = {
  jumbo: 'JUM',
  longhole: 'LHL',
  lhd: 'LHD',
  truck: 'TRK',
  sdrill: 'SDR',
};

/** The id of the operator who normally drives the `slot`-th machine of a class on a crew. */
export const operatorId = (mine: MineCode, cls: MachineClass, crew: CrewCode, slot: number): string =>
  `OP-${mine}-${CLASS_TAG[cls]}-${crew}${slot + 1}`;

/**
 * The nine operators who exist in `people.json` `people`. Each is placed on
 * the roster slot of a machine their title and employer match.
 */
export const DIRECTORY_OPERATORS: readonly { readonly name: string; readonly id: string; readonly title: string }[] = [
  { name: 'Sam Okafor', id: 'OP-KG-LHD-A1', title: 'LHD Operator, Kalbarri Gold' },
  { name: 'Tevita Fifita', id: 'OP-BR-JUM-A1', title: 'Jumbo Operator, Broken Ridge' },
  // Day-shift driver of T-09 — the contrast to the night-shift story operator.
  { name: 'Elin Berg', id: 'OP-MI-TRK-A2', title: 'Truck Operator, Redgum Mining Services' },
  { name: 'Tahlia Brooks', id: 'OP-OH-SDR-A1', title: 'Drill Operator, Ochre Hill Gold' },
  { name: 'Wes Carrington', id: 'OP-GD-SDR-A1', title: 'Drill Operator, Gawler Deep' },
  { name: 'Aroha Ngata', id: 'OP-GD-JUM-A1', title: 'Jumbo Operator, Spinifex Underground' },
  { name: 'Ollie Sandoval', id: 'OP-GD-LHD-A1', title: 'LHD Operator, Spinifex Underground' },
  { name: 'Mia Thorsen', id: 'OP-MR-LHD-A1', title: 'LHD Operator, Western Tiers Mining' },
  { name: 'Kane Ropata', id: 'OP-MR-JUM-A1', title: 'Jumbo Operator, Western Tiers Mining' },
];

/**
 * Story operators — synthesised, named here rather than drawn from the pools
 * so the walkthrough can refer to them.
 */
export const STORY_OPERATORS: readonly { readonly name: string; readonly id: string }[] = [
  // T-09's night-shift driver on the Redgum B swing.
  { name: 'Brodie Keane', id: 'OP-MI-TRK-B2' },
  // L-11's day-shift driver on the Redgum A swing.
  { name: 'Jarrah McCall', id: 'OP-MI-LHD-A2' },
];

// Pools avoid every first name and surname in the `people.json` directory, so
// a stand-in can never be mistaken for a real contact.
const FIRST = [
  'Aiden', 'Bec', 'Corey', 'Dana', 'Eli', 'Fiona', 'Gus', 'Hayley', 'Isaac', 'Jess',
  'Kobe', 'Lachlan', 'Maddie', 'Nate', 'Olivia', 'Paddy', 'Quinlan', 'Riley', 'Sasha', 'Toby',
  'Uma', 'Vince', 'Willa', 'Xavier', 'Yasmin', 'Zac', 'Ari', 'Brooke', 'Cody', 'Dimi',
  'Erin', 'Finn', 'Georgie', 'Hamid', 'Indi', 'Joel', 'Kira', 'Luka', 'Mele', 'Nico',
  'Oscar', 'Petra', 'Reuben', 'Siosaia', 'Tash', 'Viliami', 'Wiremu', 'Zara',
] as const;

const LAST = [
  'Abbott', 'Barlow', 'Cassidy', 'Dempsey', 'Egan', 'Fanning', 'Gallagher', 'Hollis', 'Ingram', 'Jansen',
  'Kovac', 'Lowe', 'Moana', 'Nguyen', "O'Brien", 'Pham', 'Quayle', 'Rasmussen', 'Sato', 'Tapsell',
  'Upton', 'Vella', 'Hunt', 'Yates', 'Zappia', 'Ashby', 'Brennan', 'Coutts', 'Duffy', 'Everett',
  'Faleolo', 'Goss', 'Hakopa', 'Iqbal', 'Jovanovic', 'Kahu', 'Latu', 'Macleod', 'Norris', 'Otene',
  'Petrakis', 'Rahman', 'Singh', 'Tuala', 'Vaughan', 'Whitlock', 'Yoon', 'Zeller',
] as const;

/** Names already taken — the directory and the story operators. */
const TAKEN = new Set<string>([
  ...DIRECTORY_OPERATORS.map((o) => o.name),
  ...STORY_OPERATORS.map((o) => o.name),
]);

/** A deterministic, collision-free stand-in name for an operator id. */
function standInName(id: string, used: Set<string>): string {
  let a = Math.floor(unit(`${id}:first`) * FIRST.length);
  let b = Math.floor(unit(`${id}:last`) * LAST.length);
  for (let attempt = 0; attempt < FIRST.length * LAST.length; attempt += 1) {
    const name = `${FIRST[a]} ${LAST[b]}`;
    if (!used.has(name)) return name;
    b = (b + 1) % LAST.length;
    if (b === 0) a = (a + 1) % FIRST.length;
  }
  throw new Error(`operators seed: name pools exhausted at ${id}`);
}

/** Secondary ticket an operator of a class may also hold. */
const CROSS_TICKET: Readonly<Partial<Record<MachineClass, MachineClass>>> = {
  lhd: 'truck',
  truck: 'lhd',
  jumbo: 'longhole',
  longhole: 'jumbo',
};

const CLASS_ORDER: readonly MachineClass[] = ['jumbo', 'longhole', 'lhd', 'truck', 'sdrill'];
const MINE_ORDER: readonly MineCode[] = ['KG', 'BR', 'MI', 'OH', 'GD', 'MR'];

function buildOperators(): OperatorRecord[] {
  const directory = new Map(DIRECTORY_OPERATORS.map((o) => [o.id, o.name]));
  const story = new Map(STORY_OPERATORS.map((o) => [o.id, o.name]));
  const used = new Set(TAKEN);
  const out: OperatorRecord[] = [];
  for (const mine of MINE_ORDER) {
    for (const cls of CLASS_ORDER) {
      const machines = FLEET.filter((f) => f.mine === mine && f.cls === cls);
      for (const crew of CREWS) {
        machines.forEach((m, slot) => {
          const id = operatorId(mine, cls, crew, slot);
          const real = directory.get(id);
          const name = real ?? story.get(id) ?? standInName(id, used);
          used.add(name);
          const cross = CROSS_TICKET[cls];
          const classes: MachineClass[] = cross && unit(`${id}:ticket`) < 0.4 ? [cls, cross] : [cls];
          out.push({
            id,
            name,
            mine,
            classes,
            crew,
            hireYear: 2012 + Math.floor(unit(`${id}:hire`) * 14),
            synthesised: real === undefined,
            homeMachine: m.machine_id,
            org: m.operated_by,
          });
        });
      }
    }
  }
  return out;
}

/** Every production operator, mine → class → crew → slot. 400 rows. */
export const OPERATORS: readonly OperatorRecord[] = buildOperators();

/** Operator by id. */
export const OPERATOR_BY_ID: Readonly<Record<string, OperatorRecord>> = Object.fromEntries(
  OPERATORS.map((o) => [o.id, o]),
);

// ---------------------------------------------------------------------------
// Story overrides
// ---------------------------------------------------------------------------

/**
 * A machine × shift whose operator is fixed for the whole window — no
 * seeded swaps with crew-mates. Pins only hold where the roster already puts
 * that operator's crew on that shift; a pin never invents a shift.
 */
export interface StoryPin {
  readonly machine: string;
  readonly shift: ShiftCode;
  readonly operatorId: string;
  readonly why: string;
}

/** The pinned machine-shifts. */
export const STORY_PINS: readonly StoryPin[] = [
  {
    machine: 'T-09',
    shift: 'N',
    operatorId: 'OP-MI-TRK-B2',
    why: 'Redgum B swing — Brodie Keane drives T-09 every night of the window.',
  },
  {
    machine: 'T-09',
    shift: 'D',
    operatorId: 'OP-MI-TRK-A2',
    why: 'Redgum A swing — Elin Berg drives T-09 every day of the window.',
  },
  {
    machine: 'L-11',
    shift: 'D',
    operatorId: 'OP-MI-LHD-A2',
    why: 'Redgum A swing — Jarrah McCall drives L-11 every day of the window.',
  },
];

/**
 * How a story machine's events are split across the two shifts, overriding
 * the default hours-weighted split.
 *
 * - `concentrate` — each event lands on `shift` with probability `share`
 *   (seeded), otherwise on the other shift.
 * - `spread` — each event lands on whichever of the day's two operators has
 *   the fewest of these events on this machine so far (ties → day), so the
 *   events spread as evenly across operators as the roster allows.
 */
export interface StorySplit {
  readonly machine: string;
  /** Alarm codes the split applies to; `'OP-ABUSE'` for `shift_daily.abuse`. */
  readonly codes: readonly string[];
  readonly mode: 'concentrate' | 'spread';
  readonly shift?: ShiftCode;
  readonly share?: number;
  readonly why: string;
}

/** Codes that are about the truck brake system. */
export const BRAKE_CODES: readonly string[] = ['W-BRK-TEMP', 'W-RET-DUTY', 'E-BRK-TEMP-HIGH', 'W-BRK-WEAR'];

/** The three story splits. */
export const STORY_SPLITS: readonly StorySplit[] = [
  {
    machine: 'T-09',
    codes: BRAKE_CODES,
    mode: 'concentrate',
    shift: 'N',
    share: 0.74,
    why: 'Mixed: most rear-brake exceedances fall on the night driver, while the pads are 82 % worn.',
  },
  {
    machine: 'T-23',
    codes: BRAKE_CODES,
    mode: 'spread',
    why: 'Equipment: brake exceedances spread across four different drivers on pads running ahead of interval.',
  },
  {
    machine: 'L-11',
    codes: ['OP-ABUSE'],
    mode: 'concentrate',
    shift: 'D',
    share: 0.92,
    why: 'Operator: bucket-ramming abuse concentrated on one day driver of a loader in good condition.',
  },
];

/** The three story machines, by the cause the walkthrough expects. */
export const STORY_MACHINES = {
  mixed: 'T-09',
  equipment: 'T-23',
  operator: 'L-11',
} as const;
